import React from 'react';
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import history from '../utilities/history';
import Store from '../utilities/store';
import { StateManager } from '../utilities/stateManager';
import { canAccessPhone } from '../pages/PhonePage/phone';
import {
  focusPhoneThread,
  inboundAlertCopy,
  inboundArrivals,
  shouldSurfaceInboundAlert,
  showInboundBrowserNotification,
  toastFromArrivals,
  unreadPhoneCount,
} from '../pages/PhonePage/phoneNotifications';

function viewerCanSeePhone() {
  if (!StateManager.authed) return false;
  return canAccessPhone({
    userType: StateManager.userType,
    whiteList: StateManager.whiteList,
    isAdmin: typeof StateManager.isAdmin === 'function' ? StateManager.isAdmin() : false,
  });
}

function publishUnread(count) {
  StateManager.phoneUnreadCount = count;
  if (typeof StateManager.setPhoneUnreadCount === 'function') {
    StateManager.setPhoneUnreadCount(count);
  }
}

function currentView() {
  return {
    pathname: (history.location && history.location.pathname) || '',
    visibility: typeof document !== 'undefined' ? document.visibilityState : 'visible',
  };
}

export default function PhoneInboundAlerts() {
  const [toast, setToast] = React.useState(null);

  React.useEffect(() => {
    if (!viewerCanSeePhone()) {
      publishUnread(0);
      return undefined;
    }
    let seeded = false;
    const seen = new Map();
    const unsub = Store.subscribePhoneThreads((list, failed) => {
      if (failed) return;
      const threads = Array.isArray(list) ? list : [];
      publishUnread(unreadPhoneCount(threads));
      const { next, arrivals } = inboundArrivals(seen, threads);
      seen.clear();
      next.forEach((fp, id) => seen.set(id, fp));
      if (!seeded) {
        seeded = true;
        return;
      }
      const view = currentView();
      const fresh = arrivals.filter(thread => shouldSurfaceInboundAlert(thread.id, view));
      fresh.forEach(thread => {
        const copy = inboundAlertCopy(thread);
        showInboundBrowserNotification(copy, {
          onClick: (alert) => focusPhoneThread(alert.threadId, history),
        });
      });
      const nextToast = toastFromArrivals(fresh);
      if (nextToast) setToast(nextToast);
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  const openToast = () => {
    if (!toast) return;
    const threadId = toast.threadId;
    setToast(null);
    focusPhoneThread(threadId, history);
  };

  if (!toast) return null;

  return (
    <Snackbar
      key={`${toast.threadId}:${toast.body}`}
      open
      autoHideDuration={8000}
      onClose={(event, reason) => {
        if (reason === 'clickaway') return;
        setToast(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: 2147483646 }}
    >
      <MuiAlert
        elevation={6}
        variant="filled"
        severity="info"
        onClose={() => setToast(null)}
        onClick={(event) => {
          if (event.target.closest && event.target.closest('.MuiAlert-action')) return;
          openToast();
        }}
        sx={{ cursor: 'pointer', alignItems: 'center' }}
      >
        {toast ? `${toast.title}${toast.body ? ` — ${toast.body}` : ''}` : ''}
      </MuiAlert>
    </Snackbar>
  );
}
